// path: dashboard/src/components/financeOfficer/BankBalancesView.jsx
import React from "react";
import { FiArrowUpRight, FiArrowDownLeft, FiCreditCard, FiHash, FiTarget, PieChart } from "react-icons/fi";

export default function BankBalancesView({ overviewData, projectsList, filters, transactions }) {
  if (!overviewData || !overviewData.bankBalances) return null;

  const formatCurrency = (amount) =>
    `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // --- 1. FILTER LOGIC ---
  // If a project is selected in the dashboard filters, show ONLY that bank card.
  let displayBanks = overviewData.bankBalances;
  
  if (filters.projectId) {
    const selectedProject = projectsList.find((p) => p._id === filters.projectId);
    if (selectedProject && selectedProject.budgetCode) {
      displayBanks = displayBanks.filter((bank) => bank.budgetCode === selectedProject.budgetCode);
    }
  }

  // If filtering is active and no banks match (or empty list), show a message
  if (displayBanks.length === 0 && filters.projectId) {
     return (
        <div className="text-center p-8 bg-white rounded-lg border border-dashed border-gray-300 text-gray-500">
            No budget data found for the selected project filter.
        </div>
     );
  }

  return (
    <div className="space-y-6">
      {/* Summary Header - Now shows totals for the VISIBLE/Filtered banks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
            <FiCreditCard className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Active Budget Codes</p>
            <p className="text-2xl font-bold text-gray-800">{displayBanks.length}</p>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="p-3 bg-green-100 text-green-600 rounded-full">
            <FiArrowUpRight className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Earnings (Donations)</p>
            <p className="text-lg font-bold text-gray-800">{formatCurrency(displayBanks.reduce((sum, b) => sum + b.totalIncome, 0))}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="p-3 bg-red-100 text-red-600 rounded-full">
            <FiArrowDownLeft className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Expenses</p>
            <p className="text-lg font-bold text-gray-800">{formatCurrency(displayBanks.reduce((sum, b) => sum + b.totalExpense, 0))}</p>
          </div>
        </div>
      </div>

      {/* Bank Accounts Grid */}
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <FiHash /> Budget Code Balances
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayBanks.map((bank) => {
            // Find the project to get the Target Budget
            const project = projectsList.find((p) => p.budgetCode === bank.budgetCode);
            const title = project ? project.projectTitle : "Unassigned Project";
            
            // Budget Calculations
            const targetBudget = project ? (project.projectBudget || 0) : 0;
            const remainingBudget = targetBudget - bank.totalExpense;
            
            // Colors
            const balanceColor = bank.balance >= 0 ? "text-green-600" : "text-red-600";
            const budgetStatusColor = remainingBudget >= 0 ? "text-blue-600" : "text-red-600";
            
            // Utilization
            const utilization = targetBudget > 0 
              ? (bank.totalExpense / targetBudget) * 100 
              : 0;

            return (
              <div key={bank.budgetCode} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden flex flex-col">
                <div className="p-5 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-mono font-semibold text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded shadow-sm">
                      {bank.budgetCode}
                    </span>
                    <span className="text-xs text-gray-400">{bank.transactionCount} Txns</span>
                  </div>
                  <h4 className="font-bold text-gray-800 leading-tight text-sm" title={title}>
                    {title}
                  </h4>
                </div>

                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  
                  {/* Row 1: Target Budget vs Actual Expense */}
                  <div>
                    <div className="flex justify-between items-end mb-1">
                        <span className="text-xs text-gray-500 uppercase font-semibold">Target Budget</span>
                        <span className="text-xs font-bold text-gray-700">{formatCurrency(targetBudget)}</span>
                    </div>
                    <div className="flex justify-between items-end mb-1">
                        <span className="text-xs text-gray-500 uppercase font-semibold">Total Spent</span>
                        <span className="text-xs font-bold text-red-600">{formatCurrency(bank.totalExpense)}</span>
                    </div>
                    {/* Budget Progress Bar */}
                    {targetBudget > 0 && (
                        <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                            <div 
                                className={`h-1.5 rounded-full ${utilization > 100 ? 'bg-red-500' : 'bg-blue-500'}`} 
                                style={{ width: `${Math.min(utilization, 100)}%` }}
                            ></div>
                        </div>
                    )}
                  </div>

                  {/* Row 2: Earnings & Balance */}
                  <div className="grid grid-cols-2 gap-2 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <div>
                        <p className="text-[10px] text-gray-500 uppercase">Earnings</p>
                        <p className="text-sm font-bold text-green-600">{formatCurrency(bank.totalIncome)}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] text-gray-500 uppercase">Bank Bal.</p>
                        <p className={`text-sm font-bold ${balanceColor}`}>{formatCurrency(bank.balance)}</p>
                    </div>
                  </div>

                  {/* Row 3: Remaining Project Budget */}
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500 font-medium">Remaining Budget</span>
                        <span className={`text-sm font-bold ${budgetStatusColor}`}>
                            {formatCurrency(remainingBudget)}
                        </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}